import { useMemo } from 'react';

const NO_RANGES = Object.freeze([]);

export function activeSelectionRange(ranges) {
    return ranges && ranges.length ? ranges[ranges.length - 1] : null;
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

/**
 * One rectangular, Excel-style selection kept in TanStack Table's cell
 * selection state: the range's anchor is the active cell and Shift moves its
 * focus corner. Positions are display indexes into `rowIds` (current sort and
 * filter) and `columnIds` (visible columns, pinned first), the same order the
 * table resolves selection bounds in.
 */
export default function useSpreadsheetSelection({
    table,
    rowIds,
    columnIds,
    isColumnSelectable,
}) {
    const rowIndexById = useMemo(
        () => new Map(rowIds.map((id, index) => [id, index])),
        [rowIds],
    );
    const columnIndexById = useMemo(
        () => new Map(columnIds.map((id, index) => [id, index])),
        [columnIds],
    );
    const selectableColumnIndexes = useMemo(
        () => columnIds.flatMap((id, index) => (isColumnSelectable(id) ? [index] : [])),
        [columnIds, isColumnSelectable],
    );

    const rowCount = rowIds.length;
    const firstColumnIndex = selectableColumnIndexes[0] ?? null;
    const lastColumnIndex = selectableColumnIndexes[selectableColumnIndexes.length - 1] ?? null;
    const readRanges = () => table.atoms.cellSelection?.get() ?? NO_RANGES;
    const isSelectableColumnIndex = (columnIndex) => (
        columnIndex != null && isColumnSelectable(columnIds[columnIndex])
    );
    const locate = (rowId, columnId) => {
        const rowIndex = rowIndexById.get(rowId);
        const columnIndex = columnIndexById.get(columnId);
        if (rowIndex == null || columnIndex == null) return null;
        return { rowId, columnId, rowIndex, columnIndex };
    };
    const cellAt = (rowIndex, columnIndex) => ({
        rowId: rowIds[rowIndex],
        columnId: columnIds[columnIndex],
        rowIndex,
        columnIndex,
    });
    const getActiveCell = (ranges = readRanges()) => {
        const range = activeSelectionRange(ranges);
        return range ? locate(range.anchorRowId, range.anchorColumnId) : null;
    };
    const getFocusCell = (ranges = readRanges()) => {
        const range = activeSelectionRange(ranges);
        return range ? locate(range.focusRowId, range.focusColumnId) : null;
    };
    const getBounds = () => {
        const ranges = readRanges();
        const active = getActiveCell(ranges);
        if (!active) return null;
        const focus = getFocusCell(ranges) || active;
        return {
            minRowIndex: Math.min(active.rowIndex, focus.rowIndex),
            maxRowIndex: Math.max(active.rowIndex, focus.rowIndex),
            minColumnIndex: Math.min(active.columnIndex, focus.columnIndex),
            maxColumnIndex: Math.max(active.columnIndex, focus.columnIndex),
        };
    };
    const select = (anchor, focus = anchor) => {
        table.setCellSelection([{
            anchorRowId: anchor.rowId,
            anchorColumnId: anchor.columnId,
            focusRowId: focus.rowId,
            focusColumnId: focus.columnId,
        }]);
    };
    const tanStackCell = (rowId, columnId) => (
        table.getRowModel().rowsById[rowId]?.getAllCellsByColumnId()[columnId]
    );

    return {
        rowCount,
        firstColumnIndex,
        lastColumnIndex,
        locate,
        cellAt,
        getActiveCell,
        getFocusCell,
        getBounds,
        select,
        columnIndexOf: (columnId) => columnIndexById.get(columnId) ?? null,
        isSelectableColumn: (columnId) => isColumnSelectable(columnId),
        isSelectableColumnIndex,
        clampRowIndex: (rowIndex) => clamp(rowIndex, 0, Math.max(0, rowCount - 1)),
        /** The next selectable column from `columnIndex` in `delta`'s direction, or null at the edge. */
        stepColumn: (columnIndex, delta) => {
            for (let index = columnIndex + delta; index >= 0 && index < columnIds.length; index += delta) {
                if (isSelectableColumnIndex(index)) return index;
            }
            return null;
        },
        isSingleCell: () => {
            const bounds = getBounds();
            return Boolean(bounds)
                && bounds.minRowIndex === bounds.maxRowIndex
                && bounds.minColumnIndex === bounds.maxColumnIndex;
        },
        /** Collapse a range to its active cell; false when there was nothing to collapse. */
        collapse: () => {
            const active = getActiveCell();
            const focus = getFocusCell();
            if (!active || !focus || (active.rowId === focus.rowId && active.columnId === focus.columnId)) {
                return false;
            }
            select(active);
            return true;
        },
        selectAll: () => table.selectAllCells(),
        /** Every selectable cell inside `bounds`, row by row. */
        cellsIn: (bounds) => {
            const cells = [];
            if (!bounds) return cells;
            for (let rowIndex = bounds.minRowIndex; rowIndex <= bounds.maxRowIndex; rowIndex += 1) {
                for (let columnIndex = bounds.minColumnIndex; columnIndex <= bounds.maxColumnIndex; columnIndex += 1) {
                    if (isSelectableColumnIndex(columnIndex)) cells.push(cellAt(rowIndex, columnIndex));
                }
            }
            return cells;
        },
        // Mouse selection uses TanStack's own handlers: mousedown starts or
        // Shift-extends the range and owns the document mouseup that ends a
        // drag; entering another cell during the drag extends it.
        startPointerSelection: (rowId, columnId, event) => {
            tanStackCell(rowId, columnId)?.getSelectionStartHandler()(event);
        },
        extendPointerSelection: (rowId, columnId, event) => {
            tanStackCell(rowId, columnId)?.getSelectionExtendHandler()(event);
        },
    };
}
